import { useEffect, useRef } from 'react'
import Icon from './Icon'
import styles from './DuplicateSkillModal.module.css'

/**
 * DuplicateSkillModal — warning shown as soon as Approve is pressed on a proposed skill
 * whose name already belongs to an active skill. Approving would create two copies, so
 * the only forward path offered is a merge: the proposal's evidence cases are linked to
 * the existing skill and the proposal is removed.
 *
 * Props:
 *   candidateName {string}  — the proposed skill's name
 *   existing      {object}  — the live skill it collides with: { id, name, type, agent_count }
 *   busy          {boolean} — merge in flight (disables both buttons)
 *   error         {string}  — in-dialog error from a failed merge (stays open)
 *   onMerge       {function}
 *   onCancel      {function}
 */
export default function DuplicateSkillModal({
  candidateName, existing, busy = false, error = '', onMerge, onCancel,
}) {
  const mergeRef = useRef(null)
  useEffect(() => { mergeRef.current?.focus() }, [])

  const holders = existing.agent_count ?? 0
  const meta = [
    existing.type,
    `${holders} team member${holders === 1 ? '' : 's'}`,
  ].filter(Boolean).join(' · ')

  return (
    <div className={styles.backdrop}>
      <div className={styles.dialog} role="alertdialog" aria-modal="true" aria-label="Duplicate skill">
        <div className={styles.icon}><Icon name="warning" /></div>
        <span className={styles.eyebrow}>Duplicate Skill</span>
        <h2 className={styles.title}>“{existing.name}” already exists</h2>
        <p className={styles.meta}>{meta}</p>
        <p className={styles.message}>
          Approving “{candidateName}” would create a duplicate. Merging links its evidence
          cases to the existing skill and removes this proposal.
        </p>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            ref={mergeRef}
            type="button"
            className={styles.mergeBtn}
            onClick={onMerge}
            disabled={busy}
          >
            {busy ? 'Merging…' : 'Merge into Existing'}
          </button>
        </div>
      </div>
    </div>
  )
}
